import {useMemo} from 'react'
import {type TranslationObject} from '@colonist/i18n'
import {useCurrentStateValidator} from './useCurrentStateValidator.ts'
import {useGameStore} from '../GameStoreContext.ts'
import {ActionState, PlayerColor} from '@colonist/game-states'
import {LobbyImage} from '@colonist/types'
import {usePlayerValidators} from './usePlayerValidators.ts'
import {useGameValidator} from './useGameValidator.ts'

interface RushCurrentStatus {
    text: TranslationObject
    icon: LobbyImage
    color: PlayerColor
}

export const useRushCurrentStatus: () => RushCurrentStatus = () => {
    const myColor = useGameStore((state) => state.myColor)
    const {getPlayerValidator} = usePlayerValidators()
    const othersIcon = LobbyImage.Players
    const currentStateValidator = useCurrentStateValidator()
    const gameValidator = useGameValidator()

    return useMemo<RushCurrentStatus>(() => {
        const isSpectator = myColor == PlayerColor.None
        const playersTakingAction = gameValidator.getPlayersTakingAction()
        const playersInDiscardActionState = currentStateValidator.getPlayersInActionState(ActionState.SelectCardsToDiscard)
        const playersDiscarding = playersInDiscardActionState.filter((color) => playersTakingAction.includes(color))
        const isMyPlayerDiscarding = playersDiscarding.includes(myColor)

        if(isMyPlayerDiscarding) {
            const myIcon = getPlayerValidator(myColor).displayIcon
            return {
                text: {key: 'strings:game.gameState.actions.selectCardsToDiscardSelf', options: {count: playersDiscarding.length}},
                icon: myIcon,
                color: myColor,
            }
        }

        if(playersDiscarding.length > 0) {
            const otherPlayer = getPlayerValidator(playersDiscarding[0])
            const icon = playersDiscarding.length === 1 ? otherPlayer.displayIcon : LobbyImage.Players
            const color = playersDiscarding.length === 1 ? otherPlayer.color : PlayerColor.None
            return {
                text: {
                    key: 'strings:game.gameState.actions.selectCardsToDiscardOther',
                    options: {
                        count: playersDiscarding.length,
                        playerUsername: otherPlayer.username,
                    },
                },
                icon: icon,
                color: color,
            }
        }

        const isMyPlayerPlacingRobber = !isSpectator && currentStateValidator.getPlayerActionState(myColor) == ActionState.PlaceRobberOrPirate
        if(isMyPlayerPlacingRobber) {
            const myIcon = getPlayerValidator(myColor).displayIcon
            return {
                text: {key: 'strings:game.gameState.actions.placeRobberOnlySelf'},
                icon: myIcon,
                color: myColor,
            }
        }

        const otherPlayerPlacingRobber = currentStateValidator.getPlayersInActionState(ActionState.PlaceRobberOrPirate)[0]
        if(otherPlayerPlacingRobber !== undefined) {
            const otherPlayer = getPlayerValidator(otherPlayerPlacingRobber)
            return {
                text: {key: 'strings:game.gameState.actions.placeRobberOnlyOther', options: {playerUsername: otherPlayer.username}},
                icon: otherPlayer.displayIcon,
                color: otherPlayer.color,
            }
        }

        const isOnlyOtherPlayersActive = isSpectator || !currentStateValidator.currentActivePlayers.includes(myColor)
        if(isOnlyOtherPlayersActive) {
            return {
                text: {key: 'strings:game.gameState.turn.waitingForOthers'},
                icon: othersIcon,
                color: PlayerColor.None,
            }
        } else {
            const myIcon = getPlayerValidator(myColor).displayIcon
            return {
                text: {key: 'strings:game.gameState.turn.turnSelf'},
                icon: myIcon,
                color: myColor,
            }
        }
    }, [myColor, currentStateValidator, getPlayerValidator])
}
